// =========================================================================
// IssuesLiveScene — per-surface projections. Fork-owned (zone C).
//
// Transformation layer: pure functions, no React, no fetches, no mutation.
// Everything that derives a figure lives in `shared/oem-engine.ts`; this file
// only arranges what the engine returns into the shapes the scene renders —
// tab parsing, legend ordering, table rows, unique keys.
//
// Source renderers: `renderIssues` / `issueHero` / `alertCard` / `siteHeat` /
// `incidentStrip` / `watchTable` at lines 3096–3186 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
//
// @see docs/logic/issues/live.md
// =========================================================================

import type { SourceTablePayload } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import type { ConditionBandSpec } from '@/versions/v1/schema/machine';
import {
  bandsBestFirst,
  COI,
  coi,
  FLEET_RULES,
  fmt$,
  ISSUE_TABS,
  MSHORT,
  totalCostOfInaction,
  type Coi,
  type IssueTab,
  type SiteHeatRow,
  type TickerEvent,
  type WatchRow,
} from '@/versions/v1/shared/oem-engine';
import { ALERTS, type Alert } from '@/versions/v1/shared/seed/alerts';
import { BY_ID } from '@/versions/v1/data/core';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/** The demo's default tab — `let ISSUE_TAB='now'` (line 3119). */
export const DEFAULT_ISSUE_TAB: IssueTab = 'now';

/**
 * Read the tab out of `?tab=`.
 *
 * Projection rule: an unrecognised value falls back to `'now'` rather than
 * rendering an empty body, matching the demo's `renderIssues(arg)` guard.
 */
export function parseIssueTab(raw: string | null): IssueTab {
  if (raw && ISSUE_TABS.some(([key]) => key === raw)) return raw as IssueTab;
  return DEFAULT_ISSUE_TAB;
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

/** The demo's two alert groups: `crit` first, then `watch`, seed order within. */
export interface AlertSplit {
  readonly critical: readonly Alert[];
  readonly watch: readonly Alert[];
  /** `critical` then `watch` — the Alerts tab's single list. */
  readonly all: readonly Alert[];
}

/**
 * splitAlertsBySeverity() → AlertSplit
 * Filter: `sev === 'crit'` into `critical`, everything else into `watch`.
 * Sort: none — seed order is preserved inside each group, as the demo does.
 */
export function splitAlertsBySeverity(): AlertSplit {
  const critical = ALERTS.filter((a) => a.sev === 'crit');
  const watch = ALERTS.filter((a) => a.sev !== 'crit');
  return { critical, watch, all: [...critical, ...watch] };
}

// ---------------------------------------------------------------------------
// Cost of inaction — the executive figure
// ---------------------------------------------------------------------------

/** One row of the "how the cost of waiting is worked out" table. */
export interface CoiTableRow {
  readonly alertId: string;
  readonly machine: string;
  /** Short site name for the machine's mine. */
  readonly site: string;
  /** `'5 h × A$182'` — labour hours against the site rate. */
  readonly labour: string;
  readonly partsPlanned: number;
  readonly planned: number;
  readonly downHours: number;
  readonly lostRate: number;
  readonly unplanned: number;
  readonly delta: number;
}

/**
 * coiTableRows() → CoiTableRow[]
 * Filter: alerts with no `COI` row are dropped (the demo returns `''` for them).
 * Sort: seed order.
 * Derive: `labour` is the demo's `${lab} h × A$${rate}` string; the rest are
 *   the raw figures off `coi()` so the renderer owns the formatting.
 */
export function coiTableRows(): CoiTableRow[] {
  return ALERTS.map((alert) => {
    const c: Coi | null = coi(alert);
    if (!c) return null;
    return {
      alertId: alert.id,
      machine: alert.machine,
      site: MSHORT[BY_ID[alert.machine].mine],
      labour: `${c.lab} h × A$${c.rate}`,
      partsPlanned: COI[alert.id].partsP,
      planned: c.planned,
      downHours: c.hU,
      lostRate: c.lost,
      unplanned: c.unplanned,
      delta: c.delta,
    };
  }).filter((row): row is CoiTableRow => row !== null);
}

/**
 * coiDrawerPayload() → SourceTablePayload
 * Projection rule: the same rows as {@link coiTableRows}, formatted as money
 *   and shaped for the `oem-source` drawer so the exposure tile can show its
 *   own working. The demo sent this figure to a tab; the port opens the
 *   breakdown in place, which is what makes the headline number auditable.
 */
export function coiDrawerPayload(): SourceTablePayload {
  const rows = coiTableRows();
  return {
    kind: 'table',
    title: 'Cost of doing nothing · per open alert',
    columns: [
      'alert',
      'machine',
      'site',
      'labour',
      'parts_planned',
      'planned',
      'down_h',
      'lost_aud_per_h',
      'unplanned',
      'cost_of_waiting',
    ],
    rows: rows
      .map((row) => [
        row.alertId,
        row.machine,
        row.site,
        row.labour,
        fmt$(row.partsPlanned),
        fmt$(row.planned),
        row.downHours,
        fmt$(row.lostRate),
        fmt$(row.unplanned),
        fmt$(row.delta),
      ])
      .concat([
        [
          'TOTAL',
          `${rows.length} alerts`,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          fmt$(totalCostOfInaction()),
        ],
      ]),
    provenance:
      'derived · COI table (demo line 3103) × site rate card (09_costs/energy_factors.csv) · planned = labour h × site rate + parts; unplanned = downtime h × lost-production rate + parts + 1.6 × labour',
  };
}

// ---------------------------------------------------------------------------
// Site heat
// ---------------------------------------------------------------------------

/** A band with a non-zero machine count, ready to size a flex segment. */
export interface HeatSegment {
  readonly band: ConditionBandSpec;
  readonly count: number;
}

/**
 * heatSegments(row) → HeatSegment[]
 * Filter: zero-count bands are dropped — the demo emits no `<i>` for them, so
 *   the stacked bar carries only the bands a site actually has.
 * Sort: best band first (`BANDS.slice().reverse()`).
 */
export function heatSegments(row: SiteHeatRow): HeatSegment[] {
  return row.bands
    .filter((entry) => entry.n > 0)
    .map((entry) => ({ band: entry.band, count: entry.n }));
}

/** The legend under the heat strip — every band, best first. */
export function bandLegend(): ConditionBandSpec[] {
  return bandsBestFirst();
}

// ---------------------------------------------------------------------------
// Fleet rules
// ---------------------------------------------------------------------------

/** One row of "what each incident changed for the whole fleet". */
export interface FleetRuleRow {
  readonly key: string;
  readonly rule: string;
  readonly from: string;
  readonly appliesTo: string;
}

/**
 * fleetRuleRows() → FleetRuleRow[]
 * Derive: `key` joins rule + source, because two rules share the source
 *   `IR-2026-015` and the source alone is not unique.
 */
export function fleetRuleRows(): FleetRuleRow[] {
  return FLEET_RULES.map(([rule, from, appliesTo]) => ({
    key: `${from}::${rule}`,
    rule,
    from,
    appliesTo,
  }));
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

/**
 * watchRowKey(row) → string
 * Invariant: `machine_id` repeats across components and `component` repeats
 *   across machines, so only the pair is unique in the condition register.
 */
export function watchRowKey(row: WatchRow): string {
  return `${row.machine_id}::${row.component}`;
}

/**
 * tickerKey(event, index) → string
 * Invariant: two events can share a timestamp and a kind (three parts orders
 *   land on `08 Sep`), so the index is part of the key. The list is a stable
 *   sorted projection rebuilt whole on every render, so the index is safe.
 */
export function tickerKey(event: TickerEvent, index: number): string {
  return `${index}::${event.t}::${event.k}`;
}
